<template>
	<div :class="['styled-form', { 'kiosk-success': isKioskMode }]">
		<div class="mt-3">
			<div class="wrapper">
				<div class="form-content">
					<form class="p-4" >

                  <!-- Guest Hub (kiosk): the tablet belongs to the lobby, not to
                       the guest, so there is no MyPage to hand them — signing
                       one guest in on a shared screen would leave their booking
                       open for the next person. They get the thank-you and the
                       way back to the start screen instead. -->
                  <div v-if="isKioskMode" class="kiosk-thank-you" role="alert">
                     {{ $t('visitor-info-thank-you.thank-you-very-much') }}
                  </div>
						<div v-else-if="!myPage" class="alert alert-success" role="alert">{{ $t('visitor-info-thank-you.thank-you-very-much') }}</div>
                  <div v-else>
                     <div class="mb-1" role="alert">{{ $t('visitor-info-thank-you.thank-you-very-much') }}</div>
                     <button type="button" class="btn btn-primary btn-lg" style="width: 200px;" @click="goToMyPage">{{ $t('visitor-info-thank-you.go_to_my_page') }}</button>
                  </div>

                  <CheckinDetails v-if="showBookingDetails" :bookingDetails="booking_details" :hide-pass="isKioskMode" :hide-pin="isKioskMode" class="mt-2"/>
						<hr v-if="!isKioskMode">
                  
						<div class="thank-you-footer" v-html="custom_text_setting.footer"></div>

						<p align="center" class="font-weight-bold">{{ facility_name }}</p>

                  <div v-if="isKioskMode" class="text-center mt-4">
                     <button type="button" class="btn btn-kiosk-home" @click="kioskGoHome">
                        {{ $t('virtual_kiosk.back_to_start') }}
                     </button>
                  </div>
					</form>
				</div>
			</div>
		</div>

		<ShareableLinkModal v-if="showLinkModal" :facility="facility" :td="td" :rsv_no="rsv_no"/>
	</div>
</template>

<style lang="scss" scoped>
.styled-form .form-content {
	max-width: 768px;
}

/* Guest Hub (kiosk): a tablet standing in the lobby, read at arm's length and
   pressed with a finger — not a phone screen held up close. Everything on the
   last screen of the flow grows to match the rest of the kiosk, and the way
   back to the start screen is the one thing that has to be unmissable. */
.kiosk-success {
   .form-content {
      max-width: 900px;
   }

   .kiosk-thank-you {
      font-size: clamp(22px, 3vw, 30px);
      font-weight: 600;
      text-align: center;
      margin-bottom: 1.5rem;
   }

   /* CheckinDetails sizes its table inline, so the kiosk sizing has to be
      pushed past both the scoped boundary and the style attribute. */
   ::v-deep .card table {
      font-size: clamp(16px, 2.2vw, 20px) !important;
   }

   ::v-deep .card {
      padding: 0.5rem;
   }

   /* The footer is authored per facility and arrives as raw HTML, so its
      own tags are reached through the scoped boundary — and it is centred
      here rather than trusted to come centred. */
   .thank-you-footer {
      text-align: center;

      ::v-deep p,
      ::v-deep li,
      ::v-deep span {
         font-size: clamp(15px, 2vw, 19px);
      }
   }

   form > p {
      font-size: clamp(16px, 2.2vw, 20px);
   }

   .btn-kiosk-home {
      width: 100%;
      max-width: 420px;
      min-height: 72px;
      font-size: clamp(18px, 2.4vw, 24px);
      font-weight: 600;
      color: #fff;
      background-color: #1aa8de;
      border-radius: 4px;
      touch-action: manipulation;

      &:hover,
      &:active {
         color: #fff;
         background-color: #0b89bb;
      }
   }
}
</style>

<script>
import ShareableLinkModal from "./../components/shareableLinkModal";
import CheckinDetails from "../components/CheckinInformation/CheckinDetails";
import kioskValidationMixin from "../kiosk/kiosk_validation_mixin";
import t from "typy";
import { cookieHelper } from "../../../../../utils/cookieHelper";
export default {
   components: {
      ShareableLinkModal,
      CheckinDetails,
   },
   mixins: [kioskValidationMixin],
   computed: {
      // Off the kiosk the card hangs on the pass, which is what it is mostly
      // there to hand over. On the shared lobby tablet the pass is hidden
      // (hide-pass), so the card is worth showing only for the stay dates -
      // and it has to show for a guest whose booking carries no pass at all.
      showBookingDetails() {
         if (!this.booking_details) return false;
         if (this.isKioskMode) {
            return Boolean(this.booking_details.checkin_date || this.booking_details.checkout_date);
         }
         return Boolean(this.booking_details.pass);
      },
   },
   data() {
      return {
         facility: {
            name: "...",
         },
         facility_name: "...",
         custom_text_setting: {},
         is_custom: false,
         td: false,
         showLinkModal: false,
         rsv_no: null,
         myPage: false,
         booking_details: {
            pin: [

            ],
         }
      };
   },
   created() {
      if(this.$route.query.tab && this.$route.query.tab == 1) {
         // To show the back to checkin button in touch down main page (tablet flow)
         this.$router.push({ 
            name: 'checkin-facility-touch-down', 
            query: { tab: 1, complete: 1 }
         });
      }
   },
   async mounted() {
      this.facility = await this.$lionheart
         .post("/facility/list", { slug_name: this.$route.params.slug_name })
         .then((r) => this.catcher(r.data, "data[0].id", (r) => r.data[0]))
         .catch((e) => this.catcher(e));

      const myPage = await this.$lionheart
         .get("/v2/facility-settings", {
            params: {
               name: 'my-page-setting',
               facility_id: this.facility.id,
               paginate: 0
            }
         })
         .then(res => res.data.data);

      this.myPage = myPage[0]?.value.active ?? false;

      if (this.facility.error.has_error) return this.$router.push("/404");

      this.is_custom = this.facility.facility_setting.length > 0 ? true : false;
      let touchdown  = (this.$route.query.ref == "td" || this.$route.query.ref == "tdf");
      if (this.$route.query.ref && touchdown) this.td = true;

      const fac_name = this.facility.facility_name.find( (obj) => obj.language == this.$i18n.locale );
      const fac_name_en = this.facility.facility_name.find( (obj) => obj.language == "en" );
         
      if (fac_name === undefined) {
         if (fac_name_en) {
            this.facility_name = fac_name_en.name;
            
         } else {
            //fallback
            this.facility_name = this.facility.name;
            
         }
      } else {
         this.facility_name = fac_name.name;
         
      }

      // Guest Hub (kiosk): the check-in is finished. The pass and the door
      // PIN are kept off this screen (hide-pass / hide-pin) because the tablet
      // is shared, but the stay dates and the guest's name still sit there.
      // Nobody taps "back to the start screen" on their way out the door, so
      // the screen clears itself for them (8. Privacy). A completed screen
      // gets no idle warning — there is nothing left to interrupt — just
      // the plain return to the start screen a minute after it appears.
      if (this.isKioskMode) {
         await this.kioskLoadConfig(this.facility.id);
         // The check-in window can end while the guest is reading this screen,
         // and MainHotelForm's pending close died with that page load. They
         // keep the full minute either way — nothing here is worth
         // interrupting — but the trip home then has to announce the close
         // (kioskAutoReset), rather than dropping the next person on "Out of
         // Operating Time" with nothing said about why. The window is either
         // already past when this screen loads — the guest submitted after it
         // ended, which the operating rule expressly allows — or it ends while
         // they are still on it; both set the same flag.
         this.kiosk_close_pending = !this.kioskCanStartCheckin();
         this.kioskWatchCheckinWindow();
         this.kioskStartAutoReturn();
      }

      this.loadCustomTextSetting(this.$i18n.locale, this.facility.id);
      if (this.$route.params.show_shareable_link) this.showLinkModal = true

      if (t(this.$route.query, 'rsv_no').isDefined) this.rsv_no = this.$route.query.rsv_no
      if(this.$route.query.tab && this.$route.query.tab == 1) return; // do nothing when in tablet mode
      if (! this.rsv_no){ //if no reservation means walkins
         this.booking_details.pass = this.$route.query.pass
         this.booking_details.email = this.$route.query.email
      }else {
         await this.getBookingDetails()
      }
      this.trackCompletedPCIForm(this.facility.id);

      // Not on the kiosk: these cookies exist only to log the guest into
      // MyPage, which the shared tablet does not offer — and they would sit
      // there for 30 days holding one guest's email and pass while the next
      // guest checks in on the same screen.
      if (this.myPage && !this.isKioskMode) {
         const email = this.$route.query.email;
         const pass = this.$route.query.pass;

         if (email) cookieHelper.setCookie(`mypage_${this.facility.id}_e`, email, 30);
         if (pass) cookieHelper.setCookie(`mypage_${this.facility.id}_p`, pass, 30);
      }
   },
   methods: {
      closeWindow() {
         this.$router.push("/");
      },
      async loadCustomTextSetting(lang, id) {
         let params;

         if (this.is_custom && this.td == false) {
            params = {
               name: `message-precheckin-form-${lang}`,
               fallback: "message-precheckin-form-en",
            };
         } else {
            params = {
               name: `message-checkin-form-${lang}`,
               fallback: "message-checkin-form-en",
            };
         }

         const response = await this.$lionheart.get("/custom-messages/" + id, {
            params: params,
         });
         this.custom_text_setting = response.data.data.value;
      },

      goToMyPage() {
         if(this.$route.query.tCode != undefined && this.$route.query.tCode) {
            window.location = `${process.env.VUE_APP_MY_PAGE_BASE_URL}/${this.facility.slug_name}/my-page?e=${this.$route.query.email}&p=${this.$route.query.pass}&t=${this.$route.query.tCode}&ref=1`;
         } else {
            window.location = `${process.env.VUE_APP_MY_PAGE_BASE_URL}/${this.facility.slug_name}/my-page?e=${this.$route.query.email}&p=${this.$route.query.pass}&ref=1`;
         }

      },
      async getBookingDetails() {
         const details = await this.$lionheart.get('/v2/facility-bookings/f/get-booking-lock', {
            params: {
               facility_id: this.facility.id,
               reservation_number: this.rsv_no
            }
         })
         .then((res) => {
            if(res.data.length <= 0) return this.booking_detail = [];
            this.booking_details = res.data
         })
      },

      async trackCompletedPCIForm(facilityId) {
         if(t(this.$route.query, 'tCode').isDefined) {
            await this.$lionheart.post("/v2/track/guest-activity", { trackingCode: this.$route.query.tCode, facilityId, action: 'completed_pci_form', metadata: null });
         }
      },
   }
};
</script>